import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getBusinessByABN, getBusinessByName } from "../../services/abr";
import { getOrganisation } from "../../store/slices/organisationSlice";
import readableABNString from "../../utils/readableABNString";
import { isDigitsOnly } from "../../utils/isDigitsOnly";
import { registrationMessages } from "../../constants";

const SEARCH_DELAY_MS = 500;

const optionKey = (option) => `${option.abn}-${option.name}`;

const highlight = (opacity) => (theme) => alpha(theme.palette.primary.main, opacity);

// The list opens over the white card, so it's set apart from it with a border, a shadow and a gap from the field
const popupSlotProps = {
  popper: { modifiers: [{ name: "offset", options: { offset: [0, 6] } }] },
  paper: {
    elevation: 8,
    sx: { border: "1px solid", borderColor: "primary.main", borderRadius: 2 },
  },
};

const listboxSx = {
  py: 0,
  "& .MuiAutocomplete-option": {
    borderLeft: "3px solid transparent",
    "&:not(:last-of-type)": { borderBottom: "1px solid", borderBottomColor: "divider" },
    "&.Mui-focused": { bgcolor: highlight(0.08), borderLeftColor: "primary.main" },
    '&[aria-selected="true"], &[aria-selected="true"].Mui-focused': { bgcolor: highlight(0.16) },
  },
};

// ABR matches for the search text: digits are looked up as an ABN first, anything else (or an ABN with no
// match) as a business name
async function searchBusinesses(text) {
  if (isDigitsOnly.test(text)) {
    const business = await getBusinessByABN(text.replace(/\s/g, ""));

    if (business?.Abn) {
      return [
        { abn: business.Abn, name: business.EntityName, state: business.AddressState, postcode: business.AddressPostcode },
      ];
    }
  }

  const businesses = await getBusinessByName(text);

  return (businesses?.Names || []).map(({ Abn, Name, State, Postcode }) => ({
    abn: Abn,
    name: Name,
    state: State,
    postcode: Postcode,
  }));
}

// The picked business's ABR record, in the shape the server adds a new organisation from, and whether it's
// new to the portal. Null if either can't be fetched.
async function checkBusiness(dispatch, abn) {
  try {
    const [business, { data }] = await Promise.all([
      getBusinessByABN(abn),
      dispatch(getOrganisation(abn)).unwrap(),
    ]);

    if (!business?.Abn) return null;

    return {
      isNew: data.isNew,
      details: {
        name: business.EntityName,
        abn: business.Abn,
        acn: business.Acn,
        gst: business.Gst,
        entityType: business.EntityTypeName,
        businessNames: business.BusinessName,
        postCode: business.AddressPostcode,
      },
    };
  } catch (error) {
    return null;
  }
}

function pickedText({ details, isNew }) {
  const abn = `ABN ${readableABNString(details.abn)}`;

  return isNew
    ? `${abn} · New to the broker portal. It will be added once your registration is approved.`
    : `${abn} · Already on the broker portal. You’ll join it once your registration is approved.`;
}

function BusinessOption({ option, ...props }) {
  const location = [option.state, option.postcode].filter(Boolean).join(" ");

  return (
    <li {...props}>
      <Box sx={{ textAlign: "left" }}>
        <Typography variant="body2" fontWeight={500}>
          {option.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ABN {readableABNString(option.abn)}
          {location && ` · ${location}`}
        </Typography>
      </Box>
    </li>
  );
}

// Business/organisation field of the registration form. A business picked from the ABR search is checked
// (see checkBusiness) and passed to onChange as { details, isNew }; onChange(null) while there's no checked pick.
export default function OrganisationSearch({ organisation, onChange, disabled }) {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [pickedOption, setPickedOption] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState("");
  // ABR responses can come back out of order, so only those for the latest search and pick are used
  const latestSearch = useRef("");
  const latestPick = useRef(null);

  const search = useMemo(
    () =>
      debounce(async (text) => {
        const results = await searchBusinesses(text).catch(() => []);
        if (latestSearch.current !== text) return;

        setOptions(results);
        setIsSearching(false);
      }, SEARCH_DELAY_MS),
    []
  );

  useEffect(() => () => search.cancel(), [search]);

  // Starts over with option picked (or nothing), dropping any earlier pick and its error
  function resetPick(option = null) {
    latestPick.current = option;
    setPickedOption(option);
    setIsChecking(Boolean(option));
    setError("");
    onChange(null);
  }

  function handleInputChange(event, value, reason) {
    setInputValue(value);
    // Picking an option fills in its name, which isn't a new search
    if (reason === "reset") return;

    resetPick();

    const text = value.trim();
    latestSearch.current = text;

    if (!text) {
      search.cancel();
      setOptions([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    search(text);
  }

  async function handleChange(event, option) {
    resetPick(option);
    if (!option) return;

    const checked = await checkBusiness(dispatch, option.abn);
    // Replaced by a newer pick or search while it was checked
    if (latestPick.current !== option) return;

    setIsChecking(false);
    if (checked) return onChange(checked);

    setPickedOption(null);
    setError(registrationMessages.BUSINESS_CHECK_FAILED);
  }

  const helperText = error || (isChecking && "Checking this business…") || (organisation && pickedText(organisation));

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      disabled={disabled}
      options={options}
      value={pickedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      // The ABR already matched the results to the search
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name || ""}
      isOptionEqualToValue={(option, value) => optionKey(option) === optionKey(value)}
      loading={isSearching}
      loadingText="Searching…"
      noOptionsText={inputValue.trim() ? "No businesses found" : "Type an ABN or business name"}
      renderOption={(props, option) => <BusinessOption {...props} key={optionKey(option)} option={option} />}
      slotProps={popupSlotProps}
      ListboxProps={{ sx: listboxSx }}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          name="organisation"
          label="Business/organisation"
          placeholder="Search by ABN or business name"
          error={Boolean(error)}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(isSearching || isChecking) && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          FormHelperTextProps={{
            sx: {
              mt: 0.5,
              fontSize: "0.75rem",
              display: helperText ? "block" : "none",
            },
          }}
        />
      )}
      sx={{ mb: 3 }}
    />
  );
}
